import { useEffect, useState } from "react";
import { Check, ChevronDown, Loader2, RotateCcw, Upload } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { SCHEMA, CONTENT_DEFAULTS } from "../../lib/content";
import { imgSrc } from "../../data/artworks";

const inp = "w-full border border-[#15202B]/15 bg-white px-3 py-2.5 outline-none focus:border-[#15202B] text-[15px]";

const ImageField = ({ value, onChange, label, k }) => {
  const [up, setUp] = useState(false); const [err, setErr] = useState("");
  const pick = async (e) => {
    const fl = e.target.files?.[0]; if (!fl) return;
    setUp(true); setErr("");
    try { const fd = new FormData(); fd.append("file", fl); const { data } = await api.post("/admin/upload", fd); onChange(data.url); }
    catch (ex) { setErr(apiError(ex, "La photo n'a pas pu être envoyée.")); } finally { setUp(false); e.target.value = ""; }
  };
  return (
    <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-4 items-center">
      <img src={imgSrc(value)} alt={label} className="w-[88px] h-[88px] object-cover bg-[#E9E2D6]" />
      <div className="grid gap-1.5">
        <span className="text-xs tracking-[0.12em] uppercase text-[#55636F]">{label}</span>
        <label className={`btn-line justify-self-start !whitespace-normal cursor-pointer ${up ? "opacity-60 pointer-events-none" : ""}`} data-testid={`content-upload-${k}`}>
          {up ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Changer la photo
          <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" onChange={pick} />
        </label>
        {err && <span className="text-xs text-[#A2372F]">{err}</span>}
      </div>
    </div>
  );
};

export const ContentEditor = () => {
  const { settings, setSettings } = useSettings();
  const [c, setC] = useState({ ...CONTENT_DEFAULTS, ...(settings.content || {}) });
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState(null);
  useEffect(() => { setC({ ...CONTENT_DEFAULTS, ...(settings.content || {}) }); }, [settings.content]);
  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true); setMsg(null);
    const changed = Object.fromEntries(Object.entries(c).filter(([k, v]) => v.trim() && v !== CONTENT_DEFAULTS[k]));
    try { const { data } = await api.put("/admin/content", { content: changed }); setSettings((s) => ({ ...s, content: data.content || {} })); setMsg({ t: "Enregistré. Les changements sont en ligne." }); }
    catch (ex) { setMsg({ err: true, t: apiError(ex) }); } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-3 pb-24" data-testid="admin-content-editor">
      <p className="text-sm text-[#55636F]">Ouvrez une section, modifiez les textes ou les photos, puis cliquez sur Enregistrer. Un champ vidé reprend le texte d'origine.</p>
      {SCHEMA.map((s, si) => (
        <details key={s.title} className="group bg-white shadow-sm" open={si === 0}>
          <summary className="flex items-center justify-between cursor-pointer list-none px-5 py-4" data-testid={`content-section-${si}`}>
            <span className="f-display text-2xl">{s.title}</span><ChevronDown className="w-5 h-5 transition-transform group-open:rotate-180" />
          </summary>
          <div className="grid gap-4 px-5 pb-5">
            {s.fields.map(([k, label, def, type]) => type === "image" ? (
              <ImageField key={k} k={k} label={label} value={c[k]} onChange={(v) => set(k, v)} />
            ) : (
              <label key={k} className="grid gap-1.5 min-w-0 text-xs tracking-[0.12em] uppercase text-[#55636F]">
                <span className="flex items-center justify-between gap-2">{label}
                  {c[k] !== def && <button type="button" onClick={() => set(k, def)} className="normal-case tracking-normal text-[#3E6173] inline-flex items-center gap-1" aria-label="Remettre le texte d'origine"><RotateCcw className="w-3 h-3" />Texte d'origine</button>}
                </span>
                {type === "area"
                  ? <textarea className={`${inp} min-h-[96px] normal-case tracking-normal`} value={c[k]} maxLength={3000} onChange={(e) => set(k, e.target.value)} data-testid={`content-field-${k}`} />
                  : <input className={`${inp} normal-case tracking-normal`} type={type === "url" ? "url" : "text"} value={c[k]} maxLength={300} onChange={(e) => set(k, e.target.value)} data-testid={`content-field-${k}`} />}
              </label>
            ))}
          </div>
        </details>
      ))}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-[#F6F1E9]/95 backdrop-blur border-t border-[#15202B]/10">
        <div className="mx-auto max-w-5xl px-5 py-3 flex flex-wrap items-center gap-4">
          <button onClick={save} disabled={busy} className="btn-ink" data-testid="content-save-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Enregistrer</button>
          {msg && <p className={`text-sm ${msg.err ? "text-[#A2372F]" : "text-[#24502C]"}`} data-testid="content-message">{msg.t}</p>}
        </div>
      </div>
    </div>
  );
};
